/**
 * Theme behaviour. No dependencies.
 *
 * Everything here is progressive: the page is fully usable, readable and
 * navigable with this file blocked. Nothing hides content that JS then has
 * to reveal, except the scroll-reveal opt-in which only arms itself after
 * confirming it can observe.
 */

(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Header state -------------------------------------------------------- */

  const header = document.querySelector('[data-header]');

  if (header) {
    /**
     * Two thresholds, not one. Compacting removes ~58px of header, and since
     * the bar is sticky (still in flow) the document shortens by the same
     * amount and the browser nudges the scroll position. With a single
     * threshold that nudge crosses back over it, the header expands, the
     * document grows, it crosses again — a flicker loop that never settles.
     *
     * The dead band between the two is wider than the height it gains or
     * loses, so a state change can never re-trigger the opposite one.
     *
     * Expanding is pinned to the very top on purpose: growing back partway
     * up reads as the bar twitching. Growth doesn't move the scroll position
     * the way shrinking does, so a near-zero threshold is safe here.
     */
    const COMPACT_ABOVE = 72;
    const EXPAND_BELOW = 4;

    let compact = false;
    let ticking = false;

    // The bar stays put at all times; scrolling only compacts it. Hiding a
    // sticky header steals the cart from under the user mid-scroll.
    const update = () => {
      const y = window.scrollY;

      if (!compact && y > COMPACT_ABOVE) compact = true;
      else if (compact && y < EXPAND_BELOW) compact = false;

      header.toggleAttribute('data-scrolled', compact);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* Reading progress ---------------------------------------------------- */

  const progress = document.querySelector('[data-progress]');

  if (progress) {
    let ticking = false;

    const update = () => {
      const scrollable = document.body.scrollHeight - window.innerHeight;
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0;
      progress.style.setProperty('--progress', Math.min(1, Math.max(0, ratio)));
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* Fullscreen menu ----------------------------------------------------- */

  const menu = document.querySelector('[data-menu]');
  const openers = document.querySelectorAll('[data-menu-open-btn]');
  const closers = document.querySelectorAll('[data-menu-close]');

  if (menu) {
    let lastFocused = null;

    const setMenu = (open) => {
      root.toggleAttribute('data-menu-open', open);
      menu.toggleAttribute('data-open', open);
      menu.setAttribute('aria-hidden', String(!open));

      openers.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));

      if (open) {
        lastFocused = document.activeElement;
        const first = menu.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      } else if (lastFocused) {
        lastFocused.focus({ preventScroll: true });
      }
    };

    openers.forEach((btn) => btn.addEventListener('click', () => setMenu(true)));
    closers.forEach((btn) => btn.addEventListener('click', () => setMenu(false)));

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) setMenu(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.hasAttribute('data-menu-open')) {
        setMenu(false);
      }
    });

    // Keep tab focus inside the overlay while it owns the screen.
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !menu.hasAttribute('data-open')) return;

      const focusable = [...menu.querySelectorAll('a[href], button:not([disabled])')].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    setMenu(false);
  }

  /* Menu hover previews -------------------------------------------------- */

  const preview = document.querySelector('[data-menu-preview]');

  if (preview && !reduceMotion.matches) {
    const slots = preview.querySelectorAll('[data-preview-slot]');
    const links = document.querySelectorAll('[data-preview-index]');

    const show = (index) => {
      slots.forEach((slot) => {
        slot.toggleAttribute('data-active', slot.dataset.previewSlot === index);
      });
    };

    links.forEach((link) => {
      link.addEventListener('mouseenter', () => show(link.dataset.previewIndex));
      link.addEventListener('focus', () => show(link.dataset.previewIndex));
    });

    preview.addEventListener('mouseleave', () => show(null));
  }

  /* Scroll reveal -------------------------------------------------------- */

  const revealables = document.querySelectorAll('[data-reveal]');

  if (revealables.length && 'IntersectionObserver' in window) {
    // Only now is it safe to hide: we know we can un-hide.
    root.setAttribute('data-reveal-ready', '');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-revealed', '');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    revealables.forEach((el) => observer.observe(el));
  }

  /* Collection filters ---------------------------------------------------- */

  const filterForm = document.querySelector('[data-filter-form]');

  if (filterForm) {
    // Submits on change so there's no Apply button to hunt for. The noscript
    // fallback in the markup keeps it usable without this.
    let timer = null;

    const submit = () => {
      const params = new URLSearchParams();

      new FormData(filterForm).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });

      const query = params.toString();
      window.location.search = query ? `?${query}` : '';
    };

    filterForm.addEventListener('change', (event) => {
      // Number inputs fire on every keystroke; wait for a pause.
      const delay = event.target.type === 'number' ? 600 : 0;
      clearTimeout(timer);
      timer = setTimeout(submit, delay);
    });

    filterForm.addEventListener('submit', (event) => {
      event.preventDefault();
      submit();
    });
  }

  /* Variant picker -------------------------------------------------------- */

  const productEl = document.querySelector('[data-product]');

  if (productEl) {
    const dataEl = productEl.querySelector('[data-variants]');
    let variants = [];

    try {
      variants = JSON.parse(dataEl?.textContent ?? '[]');
    } catch {
      variants = [];
    }

    const idInput = productEl.querySelector('[data-variant-id]');
    const priceEl = productEl.querySelector('[data-price]');
    const compareEl = productEl.querySelector('[data-compare]');
    const stockEl = productEl.querySelector('[data-stock]');
    const addBtn = productEl.querySelector('[data-add]');
    const addText = productEl.querySelector('[data-add-text]');
    const inputs = [...productEl.querySelectorAll('[data-opt-position]')];

    const strings = {
      add: addText?.dataset.add ?? addText?.textContent.trim() ?? 'Add to cart',
      soldOut: stockEl?.dataset.soldOut ?? 'Sold out',
      inStock: stockEl?.dataset.inStock ?? stockEl?.textContent.trim() ?? '',
    };

    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency',
        currency: window.Shopify?.currency?.active ?? 'EUR',
      }).format(cents / 100);

    const selected = () =>
      inputs.filter((i) => i.checked).sort((a, b) => a.dataset.optPosition - b.dataset.optPosition).map((i) => i.value);

    const findVariant = (options) =>
      variants.find((v) => options.every((value, i) => v.options[i] === value));

    // Grey out values that produce no purchasable combination with the rest
    // of the current selection, rather than letting the shopper hit a dead end.
    const markUnavailable = () => {
      const chosen = selected();

      inputs.forEach((input) => {
        const pos = Number(input.dataset.optPosition) - 1;
        const probe = [...chosen];
        probe[pos] = input.value;
        const match = findVariant(probe);
        input.toggleAttribute('data-unavailable', !match || !match.available);
      });
    };

    const update = () => {
      const variant = findVariant(selected());

      productEl.querySelectorAll('[data-opt-value]').forEach((el, i) => {
        el.textContent = selected()[i] ?? '';
      });

      markUnavailable();

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addText) addText.textContent = strings.soldOut;
        if (stockEl) stockEl.textContent = strings.soldOut;
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);

      if (compareEl) {
        const hasCompare = variant.compare_at_price > variant.price;
        compareEl.hidden = !hasCompare;
        if (hasCompare) compareEl.textContent = money(variant.compare_at_price);
      }

      if (addBtn) addBtn.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? strings.add : strings.soldOut;
      if (stockEl) stockEl.textContent = variant.available ? strings.inStock : strings.soldOut;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    inputs.forEach((input) => input.addEventListener('change', update));
    if (inputs.length) markUnavailable();
  }

  /* Product gallery carousel --------------------------------------------- */

  /**
   * Keeps the counter and the active thumbnail in step with the swipe. The
   * carousel itself is pure CSS scroll-snap; this only reports position.
   *
   * Guarded on the stage actually scrolling sideways, so on desktop — where
   * the same markup is a plain vertical stack — nothing here ever fires.
   */
  document.querySelectorAll('[data-gallery]').forEach((stage) => {
    const slides = [...stage.querySelectorAll('.pdp__slide')];
    if (slides.length < 2) return;

    const counter = document.querySelector('[data-media-current]');
    const thumbs = [...document.querySelectorAll('.pdp__thumb')];
    let current = -1;

    const mark = (index) => {
      if (index === current || index < 0) return;
      current = index;
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
    };

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (stage.scrollWidth <= stage.clientWidth) return;
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry) => mark(slides.indexOf(entry.target)));
      },
      { root: stage, threshold: 0.6 }
    );

    slides.forEach((slide) => observer.observe(slide));
  });

  /* Product card image swap ---------------------------------------------- */

  document.querySelectorAll('[data-card-swap]').forEach((card) => {
    const swap = (state) => card.toggleAttribute('data-swapped', state);
    card.addEventListener('mouseenter', () => swap(true));
    card.addEventListener('mouseleave', () => swap(false));
    card.addEventListener('focusin', () => swap(true));
    card.addEventListener('focusout', () => swap(false));
  });
})();
